import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Flag, Shield, Ban, Eye } from "lucide-react";

const VIOLATIONS = [
  { icon: "🚫", title: "Hate Speech & Harassment", desc: "Content targeting individuals or groups based on race, religion, gender, sexual orientation, disability, or nationality. Bullying, threats, and stalking are zero-tolerance offenses." },
  { icon: "🔞", title: "Sexually Explicit Content", desc: "Nudity, sexual acts, suggestive content involving minors. Detected content is removed instantly and accounts permanently banned." },
  { icon: "⚠️", title: "Violence & Self-Harm", desc: "Graphic violence, gore, abuse of animals, glorification of suicide/self-harm. Live streams violating this will be ended." },
  { icon: "💸", title: "Fraud & Scams", desc: "Fake giveaways, phishing, impersonating Hifireels staff, fraudulent gifting, and schemes to drain user wallets are bannable." },
  { icon: "📋", title: "Copyright Infringement", desc: "Uploading copyrighted music, videos, or other IP without permission. Repeat offenders are permanently banned." },
  { icon: "🤖", title: "Spam & Bots", desc: "Mass-following, fake engagement, automated scripts, view inflation, and irrelevant promotional content." },
  { icon: "🆔", title: "Impersonation", desc: "Pretending to be another person, brand, or Hifireels official without permission. Violators lose verification and account access." },
  { icon: "👶", title: "Child Safety Violations", desc: "Any content endangering minors, including grooming, exploitation, or unsafe interactions, leads to immediate ban and law enforcement reporting." },
];

const ACTIONS = [
  { icon: Eye, title: "First Warning", desc: "Content removed + warning email" },
  { icon: Flag, title: "Account Strike", desc: "1-7 day temporary suspension" },
  { icon: Ban, title: "Permanent Ban", desc: "Account terminated, wallet forfeited, IP/device blocked" },
];

export default function Violation() {
  return (
    <div className="px-4 py-6">
      <div className="flex items-center gap-3 mb-3">
        <Link to="/settings"><ArrowLeft className="w-5 h-5"/></Link>
        <h1 className="font-outfit font-extrabold text-2xl tracking-tighter">Violation Policy</h1>
      </div>

      <div className="p-4 rounded-2xl bg-[#FF2A5F]/10 border border-[#FF2A5F]/30 mb-5 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-[#FF2A5F] flex-shrink-0 mt-0.5"/>
        <p className="text-xs text-white/80">Hifireels is a safe space. We take community guidelines seriously and act swiftly on violations. Reports are reviewed by our moderation team within 24 hours.</p>
      </div>

      <h2 className="font-outfit font-bold text-lg mb-2">Prohibited Content</h2>
      <div className="space-y-2 mb-6" data-testid="violations-list">
        {VIOLATIONS.map(v => (
          <div key={v.title} className="p-3 rounded-2xl bg-white/5 border border-white/10 flex gap-3">
            <div className="text-2xl flex-shrink-0">{v.icon}</div>
            <div>
              <div className="font-semibold text-sm">{v.title}</div>
              <div className="text-xs text-white/70 mt-0.5">{v.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <h2 className="font-outfit font-bold text-lg mb-2">Enforcement Actions</h2>
      <div className="space-y-2 mb-6">
        {ACTIONS.map(a => (
          <div key={a.title} className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF2A5F]/10 flex items-center justify-center"><a.icon className="w-4 h-4 text-[#FF2A5F]"/></div>
            <div>
              <div className="font-semibold text-sm">{a.title}</div>
              <div className="text-xs text-white/70">{a.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 rounded-2xl bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex gap-3">
        <Shield className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5"/>
        <div>
          <p className="text-sm font-semibold mb-1">How to Report</p>
          <p className="text-xs text-white/80">On any user's profile, tap the <b>three-dot menu</b> → <b>Report</b>. Provide a clear reason. For urgent safety issues, contact <b className="text-[#00E5FF]">reports@hifireels.com</b>.</p>
        </div>
      </div>
    </div>
  );
}
